import React, { useEffect, useState } from 'react';
import Page from "components/page/Page";
import { useHistory } from 'react-router';
import { t } from 'i18next';
import DataTable from 'components/dataTable/DataTable';
import { DeleteCampaign, GetAudiences, GetEmailCampaigns, IAudience, IEmailCampaign, PostCampaignDuplicate, PostEmailCampaign } from 'api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns';
import { StatusCode } from 'api/protocols';
import Card from 'components/card/Card';
import moment from 'moment';
import classNames from 'classnames';
import { ButtonNew } from 'components/buttonNew';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faPencil, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch } from 'react-redux';
import { showError, showSuccess } from 'redux/actions/ui';
import { Badge, IBadgeProps } from 'components/badge/Badge';
import _ from 'lodash';
import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { NavigationDropdownNew } from 'components/navigationDropdownNew/NavigationDropdownNew';

interface IPageState {
  campaigns: IEmailCampaign[];
  pending: boolean;
  selectedTab: TabType;
}

enum TabType {
  Campaign = 0,
  Audience,
  Segments,
};
export default function EmailCampaigns() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [state, setState] = useState<IPageState>({
    campaigns: [],
    pending: true,
    selectedTab: TabType.Campaign,
  });
  const [audiences, setAudiences] = useState<IAudience[]>([]);

  async function loadCampaigns() {
    const resp = await GetEmailCampaigns({}, false);
    if (resp.status === StatusCode.OK) {
      setState({ ...state, pending: false, campaigns: resp.data });
    } else {
      setState({ ...state, pending: false });
    }
  }

  async function loadAudiences() {
    const resp = await GetAudiences({}, false);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setAudiences(resp.data);
  }

  useEffect(() => {
    void loadCampaigns();
    void loadAudiences();
  }, []);

  const tabs = [
    {
      id: TabType.Campaign,
      content: t('Campaigns'),
    },
    {
      id: TabType.Audience,
      content: t('Smart Lists'),
    },
  ];

  async function createCampaign() {
    const resp = await PostEmailCampaign({}, true);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    history.push('/admin/marketing/email-campaigns/' + resp.data.id + '/new');
  }
  const createCampaignBtn = <ButtonNew size="medium" type="primary" onClick={createCampaign}>
    <FontAwesomeIcon icon={faPlus} className="mr-2" />
    {t('Create Campaign')}
  </ButtonNew>;


  function campaignBadgeType(campaign: IEmailCampaign): IBadgeProps['type'] {
    if (['published'].includes(campaign.status)) {
      return 'success';
    }
    if (['pending', 'scheduled'].includes(campaign.status)) {
      return 'warning';
    }

    return 'gray';
  }

  async function duplicate(campaign: IEmailCampaign) {
    const resp = await PostCampaignDuplicate({ id: campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Duplicated email campaign')));
    history.push('/admin/marketing/email-campaigns/' + resp.data.id);
  }

  async function deleteCampaign(campaign: IEmailCampaign) {
    const resp = await DeleteCampaign({ id: campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Deleted email campaign')));
    void loadCampaigns();
  }

  return <Page
    title={t('Email Marketing')}
    subtitle={t('Engage your customers with email marketing campaigns.')}
    primaryAction={state.campaigns.length === 0 && {
      content: <>
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {t('Create Campaign')}
      </>,
      action: createCampaign,
    }}
    narrow
  >
    {state.campaigns.length > 0 && <>
      <div className="flex justify-around border-b border-gray-200 mb-4">
        {tabs.map(t =>
          <div
            key={t.id}
            className={classNames('px-auto py-2 flex-auto text-center cursor-pointer', {
              'border-b-2': t.id === state.selectedTab,
            })}
            onClick={() => setState({ ...state, selectedTab: t.id })}>
            {t.content}
          </div>,
        )}
      </div>
      <Card>
        <Card.Section>

          {state.selectedTab === TabType.Segments &&
            <div>Segments</div>
          }
          {state.selectedTab === TabType.Campaign && state.campaigns.length > 0 && <>
            <div className="pb-6 flex justify-between items-end">
              <div className="space-y-2">
                <div className="font-medium">{t('My campaigns')}</div>
                <p>{t('Create and manage campaigns for all your emails.')}</p>
              </div>
              <div>
                {createCampaignBtn}
              </div>
            </div>
            <div className="**:[.ui-card,.data-table]:!overflow-visible">
              <DataTable
                columns={[
                  { label: t("Name") },
                  { label: t("Status") },
                  { label: t("Description") },
                  { label: t("Last Modified") },
                  { label: t("") },
                ]}

                loading={state.pending}>
                {state.campaigns.length > 0 ?
                  state.campaigns?.map((campaign, index) => (
                    <tr
                      key={index}
                      className="clickable"
                      onClick={() => history.push(`/admin/marketing/email-campaigns/${campaign?.id}`)}
                    >
                      <td>{campaign.name}</td>
                      <td><Badge type={campaignBadgeType(campaign)}>{_.startCase(campaign.status)}</Badge></td>
                      <td>{campaign.description.substring(0, 50)}</td>
                      <td>{moment(campaign.updated_at).format('MMMM D, YYYY [at] h:mm')}</td>
                      <td>
                        <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={<FontAwesomeIcon icon={faGear} />}
                          sections={[
                            [
                              {
                                label: t('Edit'),
                                icon: 'pencil',
                                onClick: () => history.push('/admin/marketing/email-campaigns/' + campaign.id),
                              },
                              {
                                label: t('Duplicate'),
                                icon: 'copy',
                                onClick: () => duplicate(campaign),
                              },
                              {
                                label: t('Remove'),
                                icon: 'trash',
                                onClick: () => deleteCampaign(campaign),
                              },
                            ],
                          ]}
                        >
                        </NavigationDropdownNew>
                      </td>
                    </tr>
                  ))
                  : <tr>
                    <td colSpan={3} align="center">{t('There are no email campaigns created yet.')}</td>
                  </tr>}
              </DataTable>
            </div>
          </>
          }
          {state.selectedTab === TabType.Audience &&
            <>
              <div className="pb-6 flex justify-between items-end">
                <div className="mb-2">{t('Smart Lists')}</div>
                <div>
                  <ButtonNew size="medium" type="primary" onClick={() => history.push('/admin/marketing/email-campaigns/smart-list/new')}>
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('Create Smart List')}
                  </ButtonNew>

                </div>
              </div>
              <DataTable
                columns={[
                  { label: t("Name") },
                  { label: t("Customer Count") },
                  { label: t("Rules Count") },
                  { label: t("Last Modified") },
                  { label: t("") },
                ]}
                loading={state.pending}
              >
                {audiences.map(audience =>
                  <tr
                    key={audience.title + audience.id}
                    onClick={() => history.push(`/admin/marketing/email-campaigns/smart-list/${audience?.id}`)}
                  >
                    <td>{audience.title}</td>
                    <td>{audience.included_customers_count}</td>
                    <td>{audience.filters.length}</td>
                    <td>{moment(audience.updated_at).format('MMMM D, YYYY [at] h:mm')}</td>
                    <td><FontAwesomeIcon icon={faPencil} /></td>
                  </tr>)}
              </DataTable>
            </>
          }
        </Card.Section>
      </Card>
    </>}
    {state.campaigns.length === 0 && <div className="flex flex-col items-center mt-32 gap-2">
      <FontAwesomeIcon icon={faCircleCheck} />
      <p className="font-semibold">{t('Start building your email campaign')}</p>
      <p className="text-sm">{t('Ready to bring your emails to life? Start by choosing a template and customize it to fit your vision.')}</p>
      <ButtonNew type="secondary" onClick={createCampaign}>
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {t('Get Started')}
      </ButtonNew>
    </div>}
  </Page>;
}
